import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
} from '@mui/material';
import { Field, FieldProps, useFormikContext } from 'formik';
import { camelCase } from 'lodash';
import { useCallback, useMemo } from 'react';

import { InputComponent } from '..';

export interface InputCheckGroupProps extends InputComponent {
  type?: 'checkbox' | 'radio';
  value?: string | number;
  inline?: boolean;
}

export function InputCheckGroup({
  label,
  disabled,
  hint,
  required,
  nameOverride,
  value = undefined,
  type = 'radio',
  className,
}: InputCheckGroupProps) {
  const { errors } = useFormikContext<Record<string, boolean>>();

  const validate = useCallback(
    (fieldValue: string | number | boolean | undefined) => {
      if (required && (!fieldValue || fieldValue === undefined)) {
        return `${label} is required.`;
      }
      return undefined;
    },
    [label, required],
  );

  const camelLabel = useMemo(() => camelCase(label), [label]);

  const name = useMemo(
    () => nameOverride ?? camelLabel,
    [camelLabel, nameOverride],
  );

  return (
    <FormControl error={!!errors[name]} className={className}>
      <Field name={name} validate={validate} type={type} value={value}>
        {({ field }: FieldProps) => (
          <FormControlLabel
            control={
              type === 'checkbox' ? (
                <Checkbox
                  {...field}
                  checked={!!field.checked}
                  disabled={disabled}
                />
              ) : (
                <Radio
                  {...field}
                  checked={field.value === value}
                  disabled={disabled}
                />
              )
            }
            label={
              <>
                {label}
                {required && (
                  <span style={{ color: 'red', fontWeight: 'bold' }}>
                    &nbsp;*
                  </span>
                )}
              </>
            }
          />
        )}
      </Field>
      {(hint || errors[name]) && (
        <FormHelperText>{errors[name] || hint}</FormHelperText>
      )}
    </FormControl>
  );
}
